import * as THREE from 'three';
import {
    OperationGroup,
    PreviewFormValues,
} from 'components/customer/Preview3D/types';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {ShakerDoorInfo} from 'components/customer/Preview3DCommon/helpers/shakerDoorHelper';
import {getPartAttributes} from 'components/customer/RoomPlanner/helpers/getProductAttributes';
import {
    isAppliedPanelWithWidth,
    isManualPanel,
} from 'components/customer/RoomPlanner/helpers/generateCabinetMesh';

interface ApplyOperationGroupTransformArgs {
    mesh: THREE.Group<THREE.Object3DEventMap>;
    operationGroup: OperationGroup;
    variables: TemplateVariable;
    values: PreviewFormValues;
    thickness: {
        exteriorThickness: number;
        carcaseThickness: number;
    };
    shakerDoorInfo?: ShakerDoorInfo;
    templateName: string;
}

export const applyOperationGroupTransform = ({
    mesh,
    operationGroup,
    variables,
    values,
    thickness,
    shakerDoorInfo,
    templateName,
}: ApplyOperationGroupTransformArgs) => {
    const meshPos = operationGroup.Operations[0].Insert;
    const meshRot = operationGroup.Operations[0].Rotation;
    const meshNormal = operationGroup.Operations[0].Segments[0].StartNormal;

    const matrixRot = new THREE.Matrix4();

    matrixRot.makeRotationAxis(
        new THREE.Vector3(-meshNormal.x, -meshNormal.y, -meshNormal.z),
        THREE.MathUtils.degToRad(meshRot + 90)
    );

    const matrix = new THREE.Matrix4();

    const up =
        meshNormal.z == 0
            ? new THREE.Vector3(0, 0, 1)
            : new THREE.Vector3(0, 1, 0);

    if (operationGroup.MfgOrientationID !== undefined && meshNormal.z != 0) {
        up.negate();
    }

    matrix.lookAt(
        new THREE.Vector3(0, 0, 0),
        new THREE.Vector3(meshNormal.x, meshNormal.y, meshNormal.z),
        up
    );

    matrixRot.multiply(matrix);

    matrixRot.setPosition(new THREE.Vector3(meshPos.x, meshPos.y, meshPos.z));

    mesh.applyMatrix4(matrixRot);

    const carcaseThickness =
        thickness?.carcaseThickness || values.cabinet_carc_thickness || 16.5;

    if (operationGroup.IsBifoldLeftDoor) {
        if (
            operationGroup.IsRightLShape &&
            !operationGroup.IsLeftBifoldExtension
        ) {
            const boundingBox = new THREE.Box3().setFromObject(mesh);
            const size = new THREE.Vector3();
            boundingBox.getSize(size);
            const width = size.x;
            mesh.rotation.y = -(90 * Math.PI) / 180;
            mesh.position.y += width;
        } else {
            mesh.rotation.y = (90 * Math.PI) / 180;
        }
    }

    if (
        shakerDoorInfo?.isShakerDoor &&
        variables?.is3DoorProduct &&
        !operationGroup.IsLeftBifoldExtension
    ) {
        if (operationGroup.IsRightLShape && operationGroup.IsBifoldLeftDoor) {
            mesh.position.y -= 2;
        } else if (
            !operationGroup.IsRightLShape &&
            operationGroup.IsBifoldRightDoor
        ) {
            mesh.position.x += 2;
        }
    }

    if (operationGroup.IsCornerLeftDoor || operationGroup.IsCornerRightDoor) {
        mesh.rotation.y = (45 * Math.PI) / 180;
    }

    if (operationGroup.FlipY) {
        mesh.rotation.y = (180 * Math.PI) / 180;
    }

    if (operationGroup.RotationY) {
        mesh.rotation.y = (Number(operationGroup.RotationY) * Math.PI) / 180;
    }

    if (isAppliedPanelWithWidth(templateName)) {
        mesh.rotation.y = (270 * Math.PI) / 180;
    }

    if (isManualPanel(templateName, variables)) {
        mesh.rotateX((270 * Math.PI) / 180);
        mesh.rotateZ((90 * Math.PI) / 180);
        const boundingBox = new THREE.Box3().setFromObject(mesh);
        const width = boundingBox.max.x - boundingBox.min.x;
        mesh.position.x += width;
    }

    const {
        isDrawerFront,
        isInnerDrawer,
        isBinDrawer,
        isBinInnerDrawer,
        hasInsetDrawer,
    } = getPartAttributes(operationGroup, variables, values);
    if (isInnerDrawer) {
        const isCustomInnerDrawers =
            !!values?.drawer_amount && !!values?.drawers?.length;
        if (isBinInnerDrawer && isDrawerFront) {
            meshPos.y = carcaseThickness;
            mesh.position.y = carcaseThickness;
        } else if (!isCustomInnerDrawers) {
            meshPos.y += carcaseThickness + 2;
            mesh.position.y += carcaseThickness + 2;
        }
    } else if (isBinDrawer && isDrawerFront) {
        meshPos.y -= 2;
        mesh.position.y -= 2;
    } else if (isDrawerFront) {
        meshPos.y -= 2;
        mesh.position.y -= 2;
    } else if (hasInsetDrawer) {
        meshPos.y += 4;
        mesh.position.y += 4;
    }

    return meshPos;
};
